// Data hook for the component-library version of Manage Expiring Leases
// (property-level). Composed in the same order as
// `AllManageLeaseExpirations/hooks.js`'s `useAllManageLeaseExpirations`,
// with an extra step for the info-card quick filter and the URL
// drill-down (see the conversion plan and its addendum).
import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import ExpiringLeasesService from '../../services/expiringLeasesService';
import {
  EXPIRATION_BUCKETS,
  HEADERS,
  PROPERTY_PATH_MAP as BASE_PROPERTY_PATH_MAP,
} from './constantsV2';
import {
  balanceCell,
  currencyCell,
  matchesRenewalStatus,
  NO_VALUE,
  parseDrillDownParams,
  selectQuickFilterRows,
} from './utilsV2';
import { getRenewalStatus } from './utils';
import messages from './messages';
import useIntl from '../../hooks/useIntl';
import { useParseResults } from './hooksV2.useParseResults';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { navigateToUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useUserOrganizationId from '../../hooks/useUserOrganizationId';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useHistory from '../../hooks/useHistory';

// The addendum requires the exact backend predicate for renewal-status
// filtering: the on-screen status label alone (Completed/In Process/---)
// cannot tell the four statuses apart. `filterPath` points the library's
// filter step at the `renewalStatusMeta` field (see hooksV2.useParseResults.js)
// instead of the display label, and `filterFn` runs the ported predicate.
// This is a local override kept in hooksV2.js — constantsV2.js's own
// `PROPERTY_PATH_MAP` is unchanged.
const PROPERTY_PATH_MAP = {
  ...BASE_PROPERTY_PATH_MAP,
  leaseRenewalStatus: {
    ...BASE_PROPERTY_PATH_MAP.leaseRenewalStatus,
    filterPath: 'renewalStatusMeta',
    filterFn: (selectedStatuses, meta) =>
      selectedStatuses.some((statusName) =>
        matchesRenewalStatus(meta, statusName),
      ),
  },
};

const useFetchManageExpiringLeases = ({ organizationId, propertyId }) =>
  useQuery(
    ['ExpiringLeases', organizationId, propertyId],
    () => new ExpiringLeasesService().get(organizationId, propertyId, {}),
    {
      enabled: Boolean(organizationId) && Boolean(propertyId),
      refetchOnWindowFocus: false,
    },
  );

const useRows = ({ sortedAndFilteredResults, propertyId, intl }) =>
  useMemo(
    () =>
      (sortedAndFilteredResults ?? []).map((row) => {
        const {
          isRenewal,
          isRenewalComplete,
          leaseRenewalStatus,
          unit,
          unitId,
          floorPlan,
          floorPlanId,
          resident,
          residentId,
          moveInDate,
          leaseStartDate,
          leaseEndDate,
          daysUntilLeaseEnd,
          noticeToVacate,
          doNotRenew,
          underEviction,
          marketRent,
          priorLeasedRent,
          currentLeasedRent,
          balance,
          quotingRent,
          notes,
          disableNote,
          isRenewalNoteComplete,
          renewalNoteDate,
          assignedTo,
        } = row;

        // Matches the legacy popover: the note, then the date and the author on a
        // completed renewal note, and the same placeholder when there is no note.
        const noteTooltip = notes
          ? `${notes}${
              isRenewalNoteComplete
                ? ` ${moment.utc(renewalNoteDate).format('MMM DD, YYYY')} - ${
                    assignedTo ?? ''
                  }`
                : ''
            }`
          : '(No notes)';

        return {
          leaseRenewalStatus: {
            variant: 'component',
            value: (
              <span>
                {getRenewalStatus({
                  isRenewal,
                  isRenewalComplete,
                  isCsv: false,
                })}
              </span>
            ),
            // Matches the legacy status cell's tooltip exactly (see
            // `Table/index.js`): an unstarted renewal shows "Not Started"
            // instead of the raw `---` placeholder.
            tooltipContent:
              leaseRenewalStatus === NO_VALUE
                ? 'Not Started'
                : leaseRenewalStatus,
            sx: {
              justifyContent: 'center',
              display: 'inline-flex',
            },
          },
          unit: {
            variant: 'link',
            to: `/property/${propertyId}/unit/${unitId}`,
            target: '_blank',
            value: unit,
          },
          floorPlan: {
            variant: 'link',
            to: `/property/${propertyId}/floorplan/${floorPlanId}`,
            target: '_blank',
            value: floorPlan,
          },
          resident: residentId
            ? {
                variant: 'link',
                to: `/property/${propertyId}/resident/${residentId}`,
                target: '_blank',
                value: resident,
              }
            : { value: resident },
          moveInDate: { variant: 'date', value: moveInDate ?? '' },
          leaseStartDate: { variant: 'date', value: leaseStartDate ?? '' },
          leaseEndDate: { variant: 'date', value: leaseEndDate ?? '' },
          daysUntilLeaseEnd: { variant: 'number', value: daysUntilLeaseEnd },
          noticeToVacate: {
            variant: 'boolean',
            value: noticeToVacate,
            iconName: 'CheckIcon',
            fontSize: 'large',
          },
          doNotRenew: {
            variant: 'boolean',
            value: Boolean(doNotRenew),
            iconName: 'AlertInfoIcon',
            color: 'error',
          },
          underEviction: {
            variant: 'boolean',
            value: Boolean(underEviction),
            iconName: 'AlertInfoIcon',
            color: 'error',
          },
          marketRent: currencyCell(marketRent),
          priorLeasedRent: currencyCell(priorLeasedRent),
          currentLeasedRent: currencyCell(currentLeasedRent),
          otherUnitFees: { value: NO_VALUE },
          balance: balanceCell(balance),
          quotingRent: { variant: 'currency', value: quotingRent },
          note: {
            variant: 'iconButton',
            iconName: 'NotesIcon',
            tooltipTitle: noteTooltip,
            disabled: disableNote,
          },
          actions: {
            variant: 'menu',
            iconName: 'BoltIcon',
            color: 'primary',
            closeOnClick: true,
            options: [
              {
                text: intl.formatMessage(messages.quoteLeaseRenewal),
                value: residentId,
                disabled: !residentId,
                onClick: () =>
                  navigateToUrlWithSelectedPropertyId(
                    `/generate-renewal-offer/${residentId}`,
                  ),
              },
            ],
          },
        };
      }),
    [sortedAndFilteredResults, propertyId, intl],
  );

export const useManageExpiringLeases = ({ NAME }) => {
  const intl = useIntl();
  const name = useUniqueTableName(NAME);
  const organizationId = useUserOrganizationId();
  const selectedProperty = useSelectedProperty();
  const propertyId = selectedProperty?.id;
  const history = useHistory();
  const isAllCommercial = selectedProperty?.hasCommercialFloorPlans === 'ALL';

  const { data, isLoading } = useFetchManageExpiringLeases({
    organizationId,
    propertyId,
  });

  // Captured once. Matches the legacy container's own `today` prop, which
  // is likewise a single instant captured at mount and never refreshed.
  const [today] = useState(() => new Date());

  const results = useParseResults({ results: data?.results, today });

  // Parsed once, on mount, from whichever of the three URL drill-down
  // shapes a caller supplied (legacy spec, section 3). Never re-read after
  // that, matching how the legacy container seeds its own local state.
  const [drillDownParams] = useState(() =>
    parseDrillDownParams(history?.location?.search ?? ''),
  );

  // The info-card quick filter. `null` hides the over-120-day bucket by
  // default; selecting a bucket keeps only that bucket. A `month=mtm`
  // drill-down seeds this to `monthToMonth` instead of the default.
  const [selectedBucketId, setSelectedBucketId] = useState(
    () => drillDownParams.bucketId ?? null,
  );

  // Captured once, alongside `drillDownParams` above. True when a
  // date-range or renewal-status drill-down (the Lease Expiration
  // Exposure chart's month bars, and the other two external screens)
  // supplied a range or a status. Matches the legacy screen, where that
  // drill-down cleared the day-bucket filter for the rest of the page's
  // life, even after the user later selected and deselected a card.
  const [hasRangeOrStatusDrillDown] = useState(
    () =>
      Boolean(drillDownParams.expirationRange) ||
      Boolean(drillDownParams.renewalStatus),
  );

  const handleBucketChange = useCallback((bucketId) => {
    setSelectedBucketId((previous) =>
      previous === bucketId ? null : bucketId,
    );
  }, []);

  const quickFilterResults = useMemo(
    () =>
      selectQuickFilterRows({
        rows: results,
        selectedBucketId,
        keepAllDays: hasRangeOrStatusDrillDown,
      }),
    [results, selectedBucketId, hasRangeOrStatusDrillDown],
  );

  const headers = useMemo(
    () => HEADERS({ isAllCommercial }),
    [isAllCommercial],
  );

  // The other two drill-down shapes (a renewal status, a lease-end date
  // range) seed the library's own per-column filter and date state, so the
  // user can still change or clear them afterward through that column's
  // filter/date UI, the same way they could clear the old filter drawer.
  const initialFilterState = useMemo(
    () =>
      drillDownParams.renewalStatus
        ? { leaseRenewalStatus: [drillDownParams.renewalStatus] }
        : {},
    [drillDownParams],
  );

  const initialDateState = useMemo(
    () =>
      drillDownParams.expirationRange
        ? {
            leaseEndDate: {
              fromDate: drillDownParams.expirationRange.from,
              toDate: drillDownParams.expirationRange.to,
            },
          }
        : {},
    [drillDownParams],
  );

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'leaseEndDate',
    initialOrder: 'ASC',
    initialFilterState,
    initialDateState,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: quickFilterResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const rows = useRows({ sortedAndFilteredResults, propertyId, intl });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  // One count per bucket. The counts respect the column filters, the search and
  // the date range, but NOT the selected card.
  //
  // Counting the card-filtered set instead would drive every other card to zero
  // as soon as one is selected, which leaves the user no way to read where the
  // remaining leases are before switching cards. The legacy screen always showed
  // all six counts, and these cards replaced its filter drawer, so they have to
  // stay readable. This is a deliberate difference from the portfolio screen,
  // which counts the card-filtered set.
  const columnFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const metrics = useMemo(
    () =>
      EXPIRATION_BUCKETS.map((bucket) => ({
        id: bucket.id,
        label: bucket.label,
        count: (columnFilteredResults ?? []).filter((row) =>
          bucket.test(row.daysUntilLeaseEnd),
        ).length,
      })),
    [columnFilteredResults],
  );

  const hasAnyFilters = Boolean(
    Object.keys(filterState ?? {}).length ||
      Object.keys(dateState ?? {}).length ||
      Object.keys(searchState ?? {}).length ||
      selectedBucketId,
  );

  const onCSVButtonClick = useCallback(() => {
    exportAndDownloadCSV({
      excludedHeaders: ['note', 'actions'],
      filteredHeaders,
      fileName: 'ManageLeaseExpiration',
      rows,
      hasAnyFilters,
    });
  }, [filteredHeaders, rows, hasAnyFilters]);

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleBucketChange,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    metrics,
    name,
    onCSVButtonClick,
    order,
    orderBy,
    rows,
    searchState,
    selectedBucketId,
    selectedColumns,
    // The header reads "Items Found: count of totalCount", so totalCount is the
    // size of the current card's scope, not every row fetched. With no card
    // selected that scope already excludes leases more than 120 days out, so the
    // header reads N of N when the user has filtered nothing, and only shows a
    // gap once a column filter or a search actually hides rows.
    totalCount: quickFilterResults?.length ?? 0,
  };
};
